// Sugalarm — brief "Listening" affirmation shown when the kiosk wakes to
// its wake word (cycle 100). Reuses the existing VoiceBanner/Waveform
// chrome family (same bottom-anchored translucent strip, positioned above
// the kiosk FooterTabs) rather than adding a new full-screen overlay.
//
// There is NO spoken response yet this cycle — the earcon (played in
// App.tsx) plus this banner pulse is the entire acknowledgment. Intents
// and answers are cycles 101/102.
//
// Driven off `lastWakeTs`: each new value re-shows the banner for a short
// window, then it slides back down.

import { useEffect, useRef, useState } from "react";

import type { FrameVariant } from "../../hooks/useFrameVariant";
import { Waveform } from "./Waveform";

// How long the "Listening" pulse stays up after a wake.
const SHOW_MS = 1600;

interface Props {
  /** Epoch-ms of the most recent wake event (null = never). */
  lastWakeTs: number | null;
  variant?: FrameVariant;
  /** Cycle 383: drive visibility DIRECTLY for the duration of a voice
   *  command session (listening + processing), instead of the cycle-100
   *  fixed 1.6 s pulse — a session lasts as long as the person is talking.
   *  Optional: when omitted the component behaves exactly as it did in
   *  cycle 100, so the existing wake-word mount is byte-identical. */
  active?: boolean;
}

export function WakeBanner({ lastWakeTs, variant = "kiosk", active }: Props) {
  const [pulseVisible, setPulseVisible] = useState(false);
  const lastSeenRef = useRef<number | null>(null);
  // When `active` is supplied it OWNS visibility; otherwise fall back to
  // the timed pulse.
  const controlled = active !== undefined;
  const visible = controlled ? active === true : pulseVisible;

  useEffect(() => {
    if (controlled) return;
    if (lastWakeTs == null) return;
    if (lastSeenRef.current === lastWakeTs) return;
    lastSeenRef.current = lastWakeTs;
    setPulseVisible(true);
    const id = window.setTimeout(() => setPulseVisible(false), SHOW_MS);
    return () => window.clearTimeout(id);
  }, [lastWakeTs, controlled]);

  // Keep mounted briefly past `visible` so the slide-down transition runs.
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    if (visible) {
      setMounted(true);
      return undefined;
    }
    const id = window.setTimeout(() => setMounted(false), 280);
    return () => window.clearTimeout(id);
  }, [visible]);

  if (!mounted) return null;

  const accent = "var(--accent)";
  const tint = "var(--accent-soft)";

  // Clear the kiosk FooterTabs (~64 px) / mobile tab bar so the banner
  // sits above nav — matches VoiceBanner's offsets.
  const bottomOffset =
    variant === "kiosk"
      ? 64
      : variant === "mobile"
        ? "calc(56px + env(safe-area-inset-bottom, 0px))"
        : 0;

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: "absolute",
        left: 0,
        right: 0,
        bottom: bottomOffset,
        zIndex: 200,
        transform: `translateY(${visible ? 0 : 100}%)`,
        opacity: visible ? 1 : 0,
        transition: "transform 280ms ease-out, opacity 200ms ease-out",
        pointerEvents: "none",
        background: tint,
        borderTop: `0.5px solid ${accent}55`,
        boxShadow: "0 -8px 24px rgba(0, 0, 0, 0.28)",
        backdropFilter: "blur(8px)",
        padding: "10px 18px",
        display: "flex",
        alignItems: "center",
        gap: 12,
        minHeight: 64,
      }}
    >
      <div
        style={{
          fontSize: "var(--type-floor)",
          fontWeight: 700,
          letterSpacing: "0.10em",
          textTransform: "uppercase",
          color: accent,
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          flexShrink: 0,
        }}
      >
        <span
          style={{
            width: 6,
            height: 6,
            borderRadius: 999,
            background: "currentColor",
            animation: "pulseDot 1.2s ease-in-out infinite",
          }}
        />
        Listening
      </div>
      <Waveform speaking={visible} color={accent} width={140} height={32} bars={14} />
      <div style={{ flex: 1, minWidth: 0 }} />
    </div>
  );
}
